import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Pin, Check, Trash2, MessageSquare, MessageSquareOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { RichTextDisplay } from "@/components/RichTextEditor";
import CommentThread from "@/components/CommentThread";

// Iter 236e · Bulletin bodies come from three editors:
//   • blog     → Tiptap HTML   (already rich)
//   • text     → plain textarea (may have `\n\n` paragraph breaks)
//   • video/poll → plain textarea
// Historically the last three rendered as a single <p whitespace-pre-wrap>,
// which collapses formatting even when a user pasted rich text. Normalise
// to safe HTML on read so every post type renders faithfully.
function _bodyToHtml(text) {
  if (!text) return "";
  const stripped = String(text).trim();
  if (!stripped) return "";
  // Already HTML? use as-is.
  if (/<(p|br|div|ul|ol|li|h[1-6]|blockquote|strong|em|a)\b/i.test(stripped)) return stripped;
  // Escape then rebuild paragraphs (blank line = paragraph break, single \n = <br>).
  const esc = stripped
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  return esc.split(/\n\s*\n+/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

const CAT_COLOR = {
  general: "chip-internal",
  policy: "chip-pending",
  event: "chip-approved",
  kudos: "chip-status",
  urgent: "chip-rejected",
};
const REACTIONS = ["👍", "❤️", "🎉", "🙌", "🚀", "😄"];
// Iter 236f · Extra picker options for the "+" affordance. Curated so the
// picker stays tight — enough to celebrate, sympathise, or applaud without a
// full unicode buffet. Users can still react outside this set via the base
// row.
const REACTION_EXTRAS = ["👏", "🔥", "💯", "🤝", "💡", "😍", "🥳", "😢", "😮", "🤔", "👀", "☕"];

export default function AnnouncementCard({ a, isAdmin, onAck, onDelete, onEdit }) {
  const [showComments, setShowComments] = useState(false);
  const [reactions, setReactions] = useState(a.reactions_counts || {});
  const [reactionNames, setReactionNames] = useState(a.reactions_names || {});
  const [myReaction, setMyReaction] = useState(a.my_reaction || null);
  const [showExtras, setShowExtras] = useState(false);
  const [poll, setPoll] = useState({ tally: a.poll_tally || [], total: a.poll_total || 0, my_vote: a.my_vote });

  const react = async (emoji) => {
    setShowExtras(false);
    try {
      const { data } = await api.post(`/announcements/${a.id}/react`, { emoji });
      setReactions(data.counts || {});
      setReactionNames(data.names || {});
      setMyReaction(data.my_reaction);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't react")); }
  };
  const vote = async (idx) => {
    try {
      const { data } = await api.post(`/announcements/${a.id}/poll-vote`, { option_index: idx });
      setPoll({ tally: data.tally, total: data.total, my_vote: data.my_vote });
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't vote")); }
  };

  return (
    <div
      className={`relative rounded-md border p-3 sm:p-4 pl-4 transition-all duration-200
        ${a.is_read
          ? "border-border/70 bg-background hover:border-border hover:shadow-sm"
          : "border-primary/50 bg-gradient-to-br from-primary/5 via-background to-background hover:shadow-md"}`}
      data-testid={`announcement-${a.id}`}
    >
      {/* Feb 2026 R291l · Left-edge accent — gold for pinned, primary
          for unread, subtle muted for read. Communicates priority at a
          glance without pigeon-holing the whole card. */}
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md
          ${a.pinned ? "bg-amber-400"
            : a.is_read ? "bg-border/60" : "bg-primary"}`}
        aria-hidden="true"
      />
      <div className="flex items-start gap-2 sm:gap-3">
        {a.pinned && <Pin className="w-4 h-4 text-amber-500 mt-1 shrink-0" data-testid={`announcement-pinned-${a.id}`} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h3 className="font-bold text-base sm:text-lg leading-tight" style={{ fontFamily: "var(--font-heading)" }}>{a.title}</h3>
            <span className={`chip text-[10px] ${CAT_COLOR[a.category] || ""}`}>{a.category}</span>
            <span className="chip text-[10px] chip-internal">{a.post_type || "text"}</span>
            {(a.target_roles?.length || a.target_department_ids?.length || a.target_location_ids?.length) > 0 && <span className="chip text-[10px]">targeted</span>}
            <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap">{fmtRelative(a.created_at)}</span>
          </div>
          {/* Iter 231 · Optional cover image banner (data-URI or URL). Shown for
              any non-video post that has media_url set. */}
          {a.post_type !== "video" && a.media_url && (
            <img
              src={a.media_url}
              alt=""
              className="block w-full max-h-72 object-cover border border-border mb-3"
              data-testid={`announcement-cover-${a.id}`}
            />
          )}
          {a.post_type === "blog" ? (
            <RichTextDisplay html={a.body} testid={`announcement-body-${a.id}`} />
          ) : a.post_type === "video" && a.media_url ? (
            <div data-testid={`announcement-video-${a.id}`} className="space-y-2">
              {a.body && <RichTextDisplay html={_bodyToHtml(a.body)} testid={`announcement-body-${a.id}`} />}
              <video src={a.media_url} controls className="max-w-full" />
            </div>
          ) : a.post_type === "poll" ? (
            <div data-testid={`announcement-poll-${a.id}`} className="space-y-2">
              {a.body && <div className="mb-2"><RichTextDisplay html={_bodyToHtml(a.body)} testid={`announcement-body-${a.id}`} /></div>}
              {(a.poll_options || []).map((opt, idx) => {
                const count = poll.tally?.[idx] || 0;
                const pct = poll.total ? Math.round((count / poll.total) * 100) : 0;
                const mine = poll.my_vote === idx;
                return (
                  <button key={idx} onClick={() => vote(idx)} className={`block w-full text-left border ${mine ? "border-primary" : "border-border"} p-2 hover:bg-muted/30 transition`} data-testid={`poll-option-${a.id}-${idx}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{mine ? "✓ " : ""}{opt}</span>
                      <span className="font-mono text-xs">{count} · {pct}%</span>
                    </div>
                    <div className="h-1 bg-muted mt-1"><div className="h-1 bg-primary" style={{ width: `${pct}%` }} /></div>
                  </button>
                );
              })}
              <div className="text-[10px] text-muted-foreground">{poll.total} vote{poll.total === 1 ? "" : "s"}</div>
            </div>
          ) : (
            <RichTextDisplay html={_bodyToHtml(a.body)} testid={`announcement-body-${a.id}`} />
          )}
          <div className="flex items-center gap-1 mt-3 flex-wrap relative" data-testid={`announcement-reactions-${a.id}`}>
            {/* Iter 236f · Show the base 6 always. On top, render any "custom"
                emoji (from REACTION_EXTRAS) the team has actually used so their
                count doesn't get orphaned in a picker-only panel. */}
            {[...REACTIONS, ...Object.keys(reactions).filter((em) => !REACTIONS.includes(em))].map((em) => {
              const on = myReaction === em;
              const n = reactions[em] || 0;
              const who = reactionNames[em] || [];
              const tip = who.length ? `${em}  ${who.slice(0, 8).join(", ")}${who.length > 8 ? ` +${who.length - 8}` : ""}` : `React ${em}`;
              return (
                <button
                  key={em}
                  onClick={() => react(em)}
                  className={`text-xs border px-1.5 py-0.5 rounded hover:bg-muted ${on ? "border-primary bg-primary/10" : "border-border"}`}
                  data-testid={`react-${a.id}-${encodeURIComponent(em)}`}
                  title={tip}
                >
                  {em} {n > 0 && <span className="font-mono">{n}</span>}
                </button>
              );
            })}
            <button
              onClick={() => setShowExtras((v) => !v)}
              className="text-xs border border-border px-1.5 py-0.5 rounded hover:bg-muted text-muted-foreground"
              data-testid={`react-more-${a.id}`}
              title="More reactions"
            >
              +
            </button>
            {showExtras && (
              <div
                className="absolute z-20 top-full mt-1 left-0 bg-popover border border-border p-2 shadow-lg flex flex-wrap gap-1 max-w-xs"
                data-testid={`react-extras-${a.id}`}
              >
                {REACTION_EXTRAS.filter((em) => !Object.keys(reactions).includes(em)).map((em) => (
                  <button
                    key={em}
                    onClick={() => react(em)}
                    className="text-lg hover:bg-muted px-1.5 py-0.5 rounded"
                    data-testid={`react-extra-${a.id}-${encodeURIComponent(em)}`}
                    title={`React ${em}`}
                  >
                    {em}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowExtras(false)}
                  className="text-[10px] text-muted-foreground ml-auto self-center hover:text-foreground"
                >
                  close
                </button>
              </div>
            )}
            {a.comments_enabled !== false ? (
              <button onClick={() => setShowComments((v) => !v)} className="text-xs border border-border px-1.5 py-0.5 rounded hover:bg-muted inline-flex items-center gap-1" data-testid={`announcement-toggle-comments-${a.id}`}>
                <MessageSquare className="w-3 h-3" /> Comments
              </button>
            ) : (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={`announcement-comments-off-${a.id}`}><MessageSquareOff className="w-3 h-3" /> Comments off</span>
            )}
            {/* Feb 2026 R291j · Post-level actions moved HERE from the
                floated right column so the body uses full width. The
                `ml-auto` wedge pushes them to the right edge of the
                reactions row. */}
            <div className="ml-auto flex items-center gap-1">
              {!a.is_read && (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs px-2"
                        onClick={() => onAck(a)} data-testid={`announcement-ack-${a.id}`}>
                  <Check className="w-3 h-3 mr-1" /> Mark read
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs px-2"
                          onClick={() => onEdit(a)} data-testid={`announcement-edit-${a.id}`} title="Edit">
                    <Pencil className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs px-2"
                          onClick={() => onDelete(a)} data-testid={`announcement-delete-${a.id}`} title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
          {showComments && a.comments_enabled !== false && (
            <div className="mt-3" data-testid={`announcement-comments-${a.id}`}>
              <CommentThread entityType="announcement" entityId={a.id} title="" emptyHint="Be the first to comment." testid={`announcement-thread-${a.id}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
